import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import Avatar from "../assets/AvatarProfile.png";
import { profilesEndpoint, jobsEndpoint } from "../constants";
import ProfileCompletion from "../components/ProfileCompletion";

// API Base URL - adjust as needed
const API_BASE_URL = "http://127.0.0.1:8000/api";

const Profile = () => {
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [location, setLocation] = useState("");
  const [profileInfo, setProfileInfo] = useState("");
  const [pictureFile, setPictureFile] = useState(null);
  const [bannerPictureFile, setBannerPictureFile] = useState(null);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  
  // LinkedIn integration states
  const [linkedinConnected, setLinkedinConnected] = useState(false);
  const [linkedinProfile, setLinkedinProfile] = useState(null);
  const [linkedinLoading, setLinkedinLoading] = useState(false);
  const [linkedinError, setLinkedinError] = useState(null);

  const fileInputRef = useRef(null);
  const bannerFileInputRef = useRef(null);

  const token = localStorage.getItem('token');

  // LinkedIn callback handler - memoized to prevent recreation
  const handleLinkedInCallback = useCallback(async (code, state) => {
    if (!token) {
      setLinkedinError("Please log in to connect LinkedIn");
      return;
    }

    try {
      setLinkedinLoading(true);
      setLinkedinError(null);
      
      const response = await axios.get(
        `${jobsEndpoint}linkedin/callback/`,
        {
          params: { code, state },
          headers: {
            'Authorization': `Token ${token}`,  
          },
        }
      );
      
      if (response.data.status === 'success') {
        setLinkedinConnected(true);
        setLinkedinProfile(response.data.profile);
        
        // Clean up URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Show success message
        alert("LinkedIn connected successfully!");
      } else {
        setLinkedinError(response.data.message || "Failed to connect LinkedIn");
      }
    } catch (err) {

      

    } finally {
      setLinkedinLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${profilesEndpoint}me/`, {
          headers: {
            'Authorization': `Token ${token}`,
          },
        });
        setProfileData(response.data);
        setFirstName(response.data.first_name || "");
        setLastName(response.data.last_name || "");
        setLocation(response.data.location || "");
        setProfileInfo(response.data.profile_info || "");
        setSelectedLanguages(response.data.languages?.map(lang => lang.code) || []);
        setSelectedTags(response.data.tags?.map(tag => tag.id) || []);
        
        // Check if LinkedIn is connected from profile data
        if (response.data.is_linkedin_connected) {
          setLinkedinConnected(true);
          setLinkedinProfile(response.data.is_linkedin_connected);
        }
      } catch (err) {
        setError("Failed to load profile.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    const fetchLanguagesAndTags = async () => {
      try {
        const [langResponse, tagResponse] = await Promise.all([
          axios.get(`${profilesEndpoint}languages/`, {
            headers: { 'Authorization': `Token ${token}` },
          }),
          axios.get(`${profilesEndpoint}tags/`, {
            headers: { 'Authorization': `Token ${token}` },
          })
        ]);
        setAvailableLanguages(langResponse.data.results || []);
        setAvailableTags(tagResponse.data.results || []);
      } catch (err) {
        console.error("Failed to fetch languages or tags:", err);
      }
    };

    // Check for LinkedIn OAuth callback parameters in URL
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');
    
    if (error) {
      // Handle OAuth error
      const errorDesc = urlParams.get('error_description') || 'Authorization was cancelled';
      setLinkedinError(errorDesc);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (code && state) {
      // Handle successful OAuth callback
      handleLinkedInCallback(code, state);
    }

    if (token) {
      fetchProfile();
      fetchLanguagesAndTags();
    }
  }, [token, handleLinkedInCallback]);

  // LinkedIn OAuth - Initiate connection
  const handleLinkedInConnect = async () => {
    if (!token) {
      alert("Please log in to connect LinkedIn");
      return;
    }

    try {
      setLinkedinLoading(true);
      setLinkedinError(null);
      
      const response = await axios.get(`${jobsEndpoint}linkedin/auth/`, {
        headers: {
          'Authorization': `Token ${token}`,
        },
        withCredentials: true, // Important for session cookies
      });
      
      if (response.data.authorization_url) {
        // Redirect user to LinkedIn authorization page
        window.location.href = response.data.authorization_url;
      } else {
        throw new Error("No authorization URL received");
      }
    } catch (err) {
      console.error("Failed to initiate LinkedIn OAuth:", err);
      setLinkedinError("Failed to connect to LinkedIn. Please try again.");
      setLinkedinLoading(false);
    }
  };

  // LinkedIn OAuth - Disconnect
  const handleLinkedInDisconnect = async () => {
    if (!window.confirm("Are you sure you want to disconnect LinkedIn?")) {
      return;
    }

    try {
      setLinkedinLoading(true);
      setLinkedinError(null);
      
      await axios.post(
        `${jobsEndpoint}linkedin/disconnect/`,
        {},
        {
          headers: {
            'Authorization': `Token ${token}`,
          },
        }
      );
      
      setLinkedinConnected(false);
      setLinkedinProfile(null);
      alert("LinkedIn disconnected successfully!");
    } catch (err) {
      console.error("Failed to disconnect LinkedIn:", err);
      setLinkedinError("Failed to disconnect LinkedIn. Please try again.");
    } finally {
      setLinkedinLoading(false);
    }
  };

  const handleDivClick = () => fileInputRef.current?.click();
  const handleBannerDivClick = () => bannerFileInputRef.current?.click();
  const handlePictureChange = (e) => setPictureFile(e.target.files[0]);
  const handleBannerPictureChange = (e) => setBannerPictureFile(e.target.files[0]);

  const handleLanguageChange = (e) => {
    const value = Array.from(e.target.selectedOptions, option => option.value);
    setSelectedLanguages(value);
  };

  const handleTagChange = (e) => {
    const value = Array.from(e.target.selectedOptions, option => option.value);
    setSelectedTags(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('first_name', firstName);
    formData.append('last_name', lastName);
    formData.append('location', location);
    formData.append('profile_info', profileInfo);
    if (pictureFile) formData.append('picture', pictureFile);
    if (bannerPictureFile) formData.append('banner_picture', bannerPictureFile);
    selectedLanguages.forEach(lang => formData.append('languages', lang));
    selectedTags.forEach(tag => formData.append('tags', tag));

    try {
      const response = await axios.put(`${profilesEndpoint}me/update/`, formData, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      setProfileData(response.data);
      alert("Profile updated successfully!");
    } catch (err) {
      setError("Failed to update profile.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !profileData) {
    return <div className="text-center py-8">Loading profile...</div>;
  }

  if (error && !profileData) {
    return <div className="text-center py-8 text-red-500">Error: {error}</div>;
  }

  if (!profileData) {
    return <div className="text-center py-8">No profile data available.</div>;
  }

  return (
    <div>
      <ProfileCompletion />
      
      {profileData.ai_evaluation && profileData.ai_recommendations && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6" role="alert">
          <p className="font-bold">AI Profile Evaluation:</p>
          <p>{profileData.ai_evaluation}</p>
          <p className="font-bold mt-2">AI Recommendations:</p>
          <ul className="list-disc list-inside">
            {profileData.ai_recommendations.split('\n').map((item, index) => (
              item.trim() && <li key={index}>{item.trim()}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex justify-start items-start flex-row">
        <div className="flex-1 flex-col gap-6">
          <div className="flex self-stretch justify-start items-start flex-col gap-8 pb-[48px] w-full">
            
            {/* Banner and Profile Picture Section */}
            <div className="flex self-stretch justify-start items-start flex-col gap-[48px] pb-[48px]">
              <div className="w-full relative">
                <div
                  className="w-full h-54 bg-linear-to-r from-sky-300 to-white cursor-pointer"
                  onClick={handleBannerDivClick}
                >
                  {profileData.banner_picture ? (
                    <img src={profileData.banner_picture} alt="Banner" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500">
                      Upload Banner
                    </div>
                  )}
                  <input type="file" ref={bannerFileInputRef} accept="image/*" className="hidden" onChange={handleBannerPictureChange} />
                </div>

                <div className="absolute top-[160px] left-[150px]">
                  <div className="flex items-end justify-start">
                    <div className="flex justify-center items-center relative">
                      <img src={profileData.picture || Avatar} alt="" className="w-32 h-32 rounded-full object-cover border-4 border-white" />
                      <div className="absolute bottom-[0px] right-[0px] cursor-pointer" onClick={handleDivClick}>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g clipPath="url(#clip0_1330_4937)">
                            <path d="M24.7109 5.66812C25.0149 6.40315 25.5982 6.9874 26.3328 7.29243L28.9086 8.35935C29.6437 8.66383 30.2277 9.24785 30.5322 9.98294C30.8366 10.718 30.8366 11.544 30.5322 12.279L29.466 14.853C29.1614 15.5884 29.1610 16.4151 29.4670 17.1502L30.5313 19.7234C30.6822 20.0874 30.7599 20.4777 30.76 20.8718C30.7601 21.266 30.6825 21.6562 30.5316 22.0204C30.3808 22.3845 30.1597 22.7153 29.8810 22.994C29.6022 23.2726 29.2713 23.4936 28.9072 23.6443L26.3332 24.7105C25.5982 25.0144 25.0140 25.5978 24.7089 26.3323L23.6420 28.9081C23.3375 29.6432 22.7535 30.2272 22.0184 30.5317C21.2833 30.8362 20.4574 30.8362 19.7223 30.5317L17.1484 29.4655C16.4133 29.1618 15.5877 29.1624 14.8530 29.4673L12.2772 30.5327C11.5426 30.8364 10.7173 30.8362 9.98286 30.5319C9.24837 30.2277 8.66467 29.6444 8.35998 28.9101L7.29274 26.3335C6.98882 25.5985 6.40545 25.0143 5.67088 24.7092L3.09511 23.6423C2.36034 23.338 1.77649 22.7543 1.47190 22.0196C1.16730 21.285 1.16689 20.4594 1.47075 19.7245L2.53691 17.1505C2.84064 16.4154 2.84002 15.5898 2.53518 14.8552L1.47056 12.2775C1.31961 11.9135 1.24189 11.5232 1.24183 11.1291C1.24176 10.7349 1.31936 10.3447 1.47019 9.98054C1.62101 9.61641 1.84211 9.28558 2.12085 9.00693C2.39958 8.72828 2.73049 8.50729 3.09466 8.35658L5.66859 7.29043C6.40298 6.98677 6.98688 6.40414 7.29213 5.67041L8.35905 3.09464C8.66354 2.35955 9.24756 1.77553 9.98264 1.47105C10.7177 1.16657 11.5437 1.16657 12.2787 1.47105L14.8527 2.5372C15.5878 2.84094 16.4134 2.84032 17.1481 2.53548L19.7249 1.4727C20.4599 1.16839 21.2857 1.16845 22.0206 1.47287C22.7555 1.7773 23.3395 2.36115 23.6440 3.09604L24.7113 5.67259L24.7109 5.66812Z" fill="#2E90FA" />
                            <path fillRule="evenodd" clipRule="evenodd" d="M22.2665 11.8068C22.4803 11.471 22.5519 11.0641 22.4656 10.6755C22.3794 10.287 22.1423 9.94857 21.8065 9.7348C21.4708 9.52104 21.0638 9.44941 20.6753 9.53568C20.2867 9.62194 19.9483 9.85904 19.7345 10.1948L13.8605 19.4248L11.1725 16.0648C10.924 15.754 10.5622 15.5546 10.1667 15.5105C9.77118 15.4664 9.37436 15.5813 9.06352 15.8298C8.75269 16.0783 8.55331 16.4401 8.50923 16.8356C8.46516 17.2311 8.58001 17.628 8.82852 17.9388L12.8285 22.9388C12.9776 23.1254 13.1691 23.2737 13.3870 23.3713C13.6049 23.4690 13.8430 23.5132 14.0815 23.5003C14.3200 23.4874 14.5519 23.4177 14.7580 23.2971C14.9641 23.1765 15.1385 23.0084 15.2665 22.8068L22.2665 11.8068Z" fill="white" />
                          </g>
                        </svg>
                        <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={handlePictureChange} />
                      </div>
                    </div>
                    <div className="flex justify-start flex-col gap-1">
                      <p className="text-[#181D27] text-2xl font-semibold">{profileData.first_name} {profileData.last_name}</p>
                      <p className="text-[#535862]">{profileData.email}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex self-stretch justify-start items-center flex-col">
                <div className="flex self-stretch justify-start items-start flex-row gap-6">
                  <div className="flex flex-1 justify-start items-start flex-col gap-5 pt-[64px]"></div>
                </div>
              </div>

              {/* Personal Info Form Section */}
              <div className="flex self-stretch justify-start items-start flex-col gap-8 pt-4">
                <div className="flex self-stretch justify-start items-start flex-col gap-6 px-[32px]">
                  <div className="flex self-stretch justify-start items-start flex-col gap-5">
                    <div className="flex flex-wrap self-stretch justify-start items-start flex-row gap-y-4 gap-x-8">
                      <div className="min-w-[200px] max-w-[280px] flex flex-1 justify-start items-start flex-col">
                        <span className="text-[#414651] text-sm font-semibold">Personal info</span>
                        <p className="self-stretch text-[#535862] text-sm">Update your photo and personal details.</p>
                      </div>
                      <div className="min-w-[560px] max-w-[720px] flex flex-1 justify-start items-start flex-col bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                        <form onSubmit={handleSubmit} className="flex self-stretch justify-start items-start flex-col gap-6 p-[24px]">
                          {/* Name Fields */}
                          <div className="flex self-stretch justify-start items-start flex-row gap-6">
                            <div className="flex flex-1 justify-start items-start flex-col gap-1.5">
                              <label className="text-[#414651] text-sm font-medium">First name <span className="text-[#009C97]">*</span></label>
                              <input type="text" placeholder="First name.." className="w-full outline-none py-2.5 px-3.5 text-gray-600 border border-[#D5D7DA] rounded-lg" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                            </div>
                            <div className="flex flex-1 justify-start items-start flex-col gap-1.5">
                              <label className="text-[#414651] text-sm font-medium">Last name <span className="text-[#009C97]">*</span></label>
                              <input type="text" placeholder="Last name.." className="w-full outline-none py-2.5 px-3.5 text-gray-600 border border-[#D5D7DA] rounded-lg" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                            </div>
                          </div>

                          {/* Location */}
                          <div className="flex self-stretch justify-start items-start flex-col gap-1.5">
                            <label className="text-[#414651] text-sm font-medium">Location <span className="text-[#009C97]">*</span></label>
                            <input type="text" placeholder="Location.." className="w-full outline-none py-2.5 px-3 text-gray-700 border border-[#D5D7DA] rounded-lg" value={location} onChange={(e) => setLocation(e.target.value)} />
                          </div>

                          {/* Profile Info */}
                          <div className="flex self-stretch justify-start items-start flex-col gap-1.5">
                            <label className="text-[#414651] text-sm font-medium">Profile Info</label>
                            <textarea placeholder="Tell us about yourself.." className="w-full outline-none py-2.5 px-3 text-gray-700 border border-[#D5D7DA] rounded-lg" value={profileInfo} onChange={(e) => setProfileInfo(e.target.value)} rows="4" />
                          </div>

                          {/* Languages */}
                          <div className="flex self-stretch justify-start items-start flex-col gap-1.5">
                            <label className="text-[#414651] text-sm font-medium">Languages</label>
                            <select multiple value={selectedLanguages} onChange={handleLanguageChange} className="w-full outline-none p-2 text-gray-700 border border-[#D5D7DA] rounded-lg" style={{ minHeight: "100px" }}>
                              {availableLanguages.map((lang) => (
                                <option key={lang.code} value={lang.code}>{lang.name}</option>
                              ))}
                            </select>
                          </div>

                          {/* Tags */}
                          <div className="flex self-stretch justify-start items-start flex-col gap-1.5">
                            <label className="text-[#414651] text-sm font-medium">Tags</label>
                            <select multiple value={selectedTags} onChange={handleTagChange} className="w-full outline-none p-2 text-gray-700 border border-[#D5D7DA] rounded-lg" style={{ minHeight: "100px" }}>
                              {availableTags.map((tag) => (
                                <option key={tag.id} value={tag.id}>{tag.title}</option>
                              ))}
                            </select>
                          </div>

                          {/* Form Buttons */}
                          <div className="flex self-stretch justify-end items-center flex-row gap-4">
                            <button type="button" className="py-2.5 px-3.5 border border-gray-300 rounded-lg cursor-pointer">
                              <span className="text-[#414651] text-sm font-semibold">Cancel</span>
                            </button>
                            <button type="submit" disabled={loading} className="py-2.5 px-3.5 bg-[#009C97] border-2 rounded-lg cursor-pointer disabled:opacity-50">
                              <span className="text-[#FFFFFF] text-sm font-semibold">{loading ? 'Saving...' : 'Save changes'}</span>
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>

                    <div className="self-stretch bg-[#E9EAEB] h-px"></div>

                    {/* ============= LINKEDIN INTEGRATION SECTION ============= */}
                    <div className="flex flex-wrap self-stretch justify-start items-start flex-row gap-y-4 gap-x-8">
                      <div className="min-w-[200px] max-w-[280px] flex flex-1 justify-start items-start flex-col">
                        <span className="text-[#414651] text-sm font-semibold">Integrations and connected apps</span>
                        <p className="self-stretch text-[#535862] text-sm">Supercharge your workflow and connect the tool you use every day.</p>
                      </div>
                      
                      <div className="flex justify-start items-start flex-col bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]" style={{ width: "720px" }}>
                        <div className="flex self-stretch justify-start items-start flex-col gap-6 p-[24px]">
                          
                          {/* LinkedIn Integration Card */}
                          <div className="min-w-[320px] flex flex-1 justify-start items-start flex-col bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                            <div className="flex self-stretch justify-start items-start flex-col gap-6 p-[20px]">
                              <div className="flex self-stretch justify-start items-start flex-row gap-2">
                                <div className="flex flex-1 justify-start items-center flex-row gap-3">
                                  {/* LinkedIn Logo */}
                                  <div className="flex justify-start items-center flex-row p-0.5 bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-lg shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <rect width="48" height="48" rx="4" fill="#0077B5" />
                                      <path d="M16.5 19.5H11.5V36.5H16.5V19.5Z" fill="white" />
                                      <path d="M14 11.5C12.067 11.5 10.5 13.067 10.5 15C10.5 16.933 12.067 18.5 14 18.5C15.933 18.5 17.5 16.933 17.5 15C17.5 13.067 15.933 11.5 14 11.5Z" fill="white" />
                                      <path d="M37.5 25C37.5 21.41 34.09 19 30.5 19C28.5 19 26.5 20 25.5 21.5V19.5H20.5V36.5H25.5V25.5C25.5 23.567 26.567 22.5 28.5 22.5C30.433 22.5 31.5 23.567 31.5 25.5V36.5H36.5V25H37.5Z" fill="white" />
                                    </svg>
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-[#181D27] font-semibold">LinkedIn</p>
                                    {linkedinConnected && linkedinProfile && (
                                      <p className="text-xs text-[#535862]">
                                        Connected as {linkedinProfile.name || linkedinProfile.given_name || linkedinProfile.email}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Toggle Switch */}
                                <div className="flex justify-start items-start flex-col gap-2.5">
                                  <button
                                    type="button"
                                    disabled={linkedinLoading}
                                    className={`flex items-center p-0.5 rounded-full w-[36px] h-[20px] cursor-pointer transition-colors disabled:opacity-50 ${
                                      linkedinConnected ? 'bg-[#009C97] justify-end' : 'bg-[#F5F5F5] justify-start'
                                    }`}
                                    onClick={linkedinConnected ? handleLinkedInDisconnect : handleLinkedInConnect}
                                  >
                                    <div className="bg-[#FFFFFF] rounded-full shadow-sm w-[16px] h-[16px]"></div>
                                  </button>
                                </div>
                              </div>
                              
                              <p className="self-stretch text-[#535862] text-sm">
                                {linkedinConnected 
                                  ? "Share your job posts directly to your LinkedIn profile and network."
                                  : "Connect your LinkedIn account to share job posts and expand your reach."
                                }
                              </p>

                              {/* Error Message */}
                              {linkedinError && (
                                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 5V8M8 11H8.01M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                  <span>{linkedinError}</span>
                                  <button onClick={() => setLinkedinError(null)} className="ml-auto text-red-400 hover:text-red-600">✕</button>
                                </div>
                              )}
                              
                              {/* Connection Status & Actions */}
                              {linkedinLoading ? (
                                <div className="flex items-center justify-center py-2">
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#009C97]"></div>
                                  <span className="ml-2 text-sm text-[#535862]">Connecting...</span>
                                </div>
                              ) : linkedinConnected ? (
                                <div className="flex flex-col gap-2">
                                  <div className="flex items-center gap-2 text-sm text-green-600">
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M13.3334 4L6.00002 11.3333L2.66669 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <span className="font-medium">Connected Successfully</span>
                                  </div>
                                  <button onClick={handleLinkedInDisconnect} className="text-sm text-red-600 hover:text-red-700 font-medium text-left">
                                    Disconnect LinkedIn
                                  </button>
                                </div>
                              ) : (
                                <button onClick={handleLinkedInConnect} className="flex items-center justify-center gap-2 py-2.5 px-4 bg-[#0077B5] text-white rounded-lg hover:bg-[#006399] transition-colors font-semibold text-sm">
                                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect width="20" height="20" rx="2" fill="white"/>
                                    <path d="M7 8H5V15H7V8Z" fill="#0077B5"/>
                                    <path d="M6 5C5.448 5 5 5.448 5 6C5 6.552 5.448 7 6 7C6.552 7 7 6.552 7 6C7 5.448 6.552 5 6 5Z" fill="#0077B5"/>
                                    <path d="M16 11C16 9.343 14.657 8 13 8C11.5 8 10.5 8.5 10 9V8H8V15H10V11C10 10.448 10.448 10 11 10C11.552 10 12 10.448 12 11V15H14V11C14 10.448 14.448 10 15 10" fill="#0077B5"/>
                                  </svg>
                                  Connect with LinkedIn
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Slack Integration Card */}
                          <div className="min-w-[320px] flex flex-1 justify-start items-start flex-col bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                            <div className="flex self-stretch justify-start items-start flex-col gap-6 p-[20px]">
                              <div className="flex self-stretch justify-start items-start flex-row gap-2">
                                <div className="flex flex-1 justify-start items-center flex-row gap-3">
                                  <div className="flex justify-start items-center flex-row p-0.5 bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-lg shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                                    <div className="flex justify-center items-center w-[48px] h-[48px]">
                                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M35.7503 18.4994C37.8212 18.4994 39.5 16.8206 39.5 14.7497C39.5 12.6788 37.8211 11 35.7502 11C33.6791 11 32 12.6789 32 14.75V18.4994H35.7503ZM25.25 18.4994C27.3211 18.4994 29 16.8204 29 14.7494V4.25C29 2.17893 27.3211 0.5 25.25 0.5C23.1789 0.5 21.5 2.17893 21.5 4.25V14.7494C21.5 16.8204 23.1789 18.4994 25.25 18.4994Z" fill="#2EB67D"/>
                                        <path d="M4.24968 21.5006C2.17879 21.5006 0.5 23.1794 0.5 25.2503C0.5 27.3212 2.17895 29 4.24984 29C6.32091 29 8 27.3211 8 25.25V21.5006H4.24968ZM14.75 21.5006C12.6789 21.5006 11 23.1796 11 25.2506V35.75C11 37.8211 12.6789 39.5 14.75 39.5C16.8211 39.5 18.5 37.8211 18.5 35.75V25.2506C18.5 23.1796 16.8211 21.5006 14.75 21.5006Z" fill="#E01E5A"/>
                                        <path d="M21.5003 35.7503C21.5003 37.8212 23.1791 39.5 25.25 39.5C27.3208 39.5 28.9996 37.8211 28.9996 35.7502C28.9996 33.6791 27.3207 32 25.2496 32L21.5003 32V35.7503ZM21.5003 25.25C21.5003 27.3211 23.1792 29 25.2503 29H35.7496C37.8207 29 39.4996 27.3211 39.4996 25.25C39.4996 23.1789 37.8207 21.5 35.7496 21.5H25.2503C23.1792 21.5 21.5003 23.1789 21.5003 25.25Z" fill="#ECB22E"/>
                                        <path d="M18.4996 4.24968C18.4996 2.17879 16.8208 0.5 14.7499 0.5C12.679 0.5 11.0002 2.17895 11.0002 4.24984C11.0002 6.32091 12.6792 8 14.7502 8H18.4996V4.24968ZM18.4996 14.75C18.4996 12.6789 16.8207 11 14.7496 11H4.25025C2.17918 11 0.500244 12.6789 0.500244 14.75C0.500244 16.8211 2.17918 18.5 4.25024 18.5H14.7496C16.8207 18.5 18.4996 16.8211 18.4996 14.75Z" fill="#36C5F0"/>
                                      </svg>
                                    </div>
                                  </div>
                                  <p className="flex-1 text-[#181D27] font-semibold">Slack</p>
                                </div>
                                <div className="flex justify-end items-center p-0.5 bg-[#F5F5F5] rounded-full w-[36px] h-[20px]">
                                  <div className="bg-[#FFFFFF] rounded-full shadow-sm w-[16px] h-[16px]"></div>
                                </div>
                              </div>
                              <p className="self-stretch text-[#535862] text-sm">Send notifications to channels and create projects.</p>
                            </div>
                          </div>

                          {/* Zapier Integration Card */}
                          <div className="min-w-[320px] flex flex-1 justify-start items-start flex-col bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-xl shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                            <div className="flex self-stretch justify-start items-start flex-col gap-6 p-[20px]">
                              <div className="flex self-stretch justify-start items-start flex-row gap-2">
                                <div className="flex flex-1 justify-start items-center flex-row gap-3">
                                  <div className="flex justify-start items-center p-0.5 bg-[#FFFFFF] border-solid border-[#E9EAEB] border rounded-lg shadow-[_0px_1px_2px_0px_rgba(10,13,18,0.05)]">
                                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M28.4327 28.4421C25.5832 29.5144 22.3902 29.5163 19.5406 28.4428C18.467 25.5964 18.4668 22.4039 19.539 19.5571C22.3878 18.4831 25.584 18.4829 28.4328 19.557C29.506 22.4029 29.5056 25.5961 28.4327 28.4421ZM44.708 20.4993H32.442L41.1149 11.8314C39.7538 9.92066 38.0734 8.24215 36.1614 6.88183L27.4879 15.5497V3.29165C25.173 2.90249 22.799 2.90308 20.4841 3.29165V15.5497L11.8106 6.88183C9.89908 8.24122 8.21816 9.92204 6.85693 11.8314L15.5311 20.4993H3.26525C2.96247 22.8821 2.8641 25.1214 3.26525 27.4992H15.5313L6.8571 36.1672C8.22161 38.0808 9.8957 39.7538 11.8106 41.1174L20.4841 32.4488V44.7076C22.7992 45.0953 25.1726 45.0955 27.4877 44.7076V32.4488L36.1621 41.1174C38.0749 39.7558 39.7523 38.0786 41.1149 36.1672L32.4407 27.4992H44.708C45.0973 25.1879 45.0973 22.8107 44.708 20.4993Z" fill="#FF4A00"/>
                                    </svg>
                                  </div>
                                  <p className="flex-1 text-[#181D27] font-semibold">Zapier</p>
                                </div>
                                <div className="flex justify-end items-center p-0.5 bg-[#F5F5F5] rounded-full w-[36px] h-[20px]">
                                  <div className="bg-[#FFFFFF] rounded-full shadow-sm w-[16px] h-[16px]"></div>
                                </div>
                              </div>
                              <p className="self-stretch text-[#535862] text-sm">Build custom automations and integrations with apps.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bottom Divider and Add More Button */}
                    <div className="flex self-stretch justify-start items-center flex-col gap-4 pb-4">
                      <div className="self-stretch bg-[#E9EAEB] h-px"></div>
                      <div className="flex self-stretch justify-end items-center flex-row gap-4 px-[24px]">
                        <button className="flex justify-center items-center gap-1 py-2.5 px-3.5 border border-gray-300 rounded-lg cursor-pointer">
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 4.16602V15.8327M4.16669 9.99935H15.8334" stroke="#414651" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span className="text-[#414651] text-sm font-semibold">Add More</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;